import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "@/components/ui/button";
import { useSession } from "@/services/auth0/session";
import { Link, useLocation } from "react-router";
import { ChevronRightIcon, Loader2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface LocationState {
  returnTo?: string;
}

export default function Home() {
  const { loginWithRedirect } = useAuth0();
  const { user, isLoading } = useSession();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const returnTo = locationState?.returnTo ?? "/dispatch";

  const fullName = `${user?.firstName} ${user?.lastName}`;
  const initials = `${user?.firstName?.[0] ?? ""}${user?.lastName?.[0] ?? ""}`;

  const onClick = () => {
    void loginWithRedirect({
      appState: {
        returnTo,
      },
    });
  };

  const renderButton = () => {
    if (isLoading) {
      return (
        <Button
          size="lg"
          className="p-8 w-1/3 text-lg"
          variant="default"
          disabled
        >
          <Loader2 className="w-10 h-10 animate-spin" />
        </Button>
      );
    }

    if (user) {
      return (
        <Button
          asChild
          size="lg"
          variant="default"
          className="p-8 w-1/3 text-lg"
        >
          <Link to={returnTo}>
            <Avatar>
              <AvatarImage src={user.picture} alt={fullName} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            {fullName || user.email}
            <ChevronRightIcon className="w-4 h-4 " />
          </Link>
        </Button>
      );
    }

    return (
      <Button
        size="lg"
        className="p-8 w-1/3 text-lg"
        variant="default"
        onClick={onClick}
      >
        Go
      </Button>
    );
  };

  return (
    <div className="isolate px-6 pt-14 lg:px-8 flex-col">
      <div
        className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80 "
        aria-hidden="true"
      >
        <div
          className="relative left-[calc(50%-11rem)] aspect-1155/678 w-144.5 -translate-x-1/2 rotate-30 bg-linear-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-288.75"
          style={{
            clipPath:
              "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
          }}
        />
      </div>
      <div className="mx-auto max-w-2xl py-32 sm:py-48 lg:py-56 ">
        <div className="backdrop-blur-md bg-white/30 p-8 rounded-xl shadow-lg border border-white/20 hover:bg-white/40 transition-all duration-300">
          <div className="flex justify-between items-center">
            <p className="text-2xl font-semibold text-center">
              Welcome to EZX Admin Portal
            </p>
            {renderButton()}
          </div>
        </div>
      </div>
      <div
        className="absolute inset-x-0 top-[calc(80%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(80%-30rem)]"
        aria-hidden="true"
      >
        <div
          className="relative left-[calc(50%+3rem)] aspect-1155/678 w-144.5 -translate-x-1/2 bg-linear-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%+36rem)] sm:w-288.75"
          style={{
            clipPath:
              "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
          }}
        />
      </div>
    </div>
  );
}
